import React, { useState } from 'react';
import {
  Stack,
  TextField,
  PrimaryButton,
  MessageBar,
  MessageBarType,
  DatePicker,
  IDatePickerStrings,
  IStackTokens,
  MaskedTextField,
  Spinner,
  SpinnerSize,
  Text,
} from '@fluentui/react';

// Interface for form state
interface IFormState {
  username: string;
  password: string;
  confirmPassword: string;
  dateOfBirth: Date | null;
  email: string;
  ssn: string;
  // recaptchaToken: string; // Commented out for now
}

// Interface for form errors
interface IFormErrors {
  username?: string;
  password?: string;
  confirmPassword?: string;
  dateOfBirth?: string;
  email?: string;
  ssn?: string;
  // recaptcha?: string; // Commented out for now
}

// Date picker strings configuration
const datePickerStrings: IDatePickerStrings = {
  months: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ],
  shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  goToToday: 'Go to today',
  prevMonthAriaLabel: 'Previous month',
  nextMonthAriaLabel: 'Next month',
  prevYearAriaLabel: 'Previous year',
  nextYearAriaLabel: 'Next year',
  isRequiredErrorMessage: 'Date of birth is required.',
};

// Stack tokens for spacing
const stackTokens: IStackTokens = {
  childrenGap: 15
};

// Main registration form component
const RegistrationForm: React.FC = () => {
  // Form state
  const [formState, setFormState] = useState<IFormState>({
    username: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: null,
    email: '',
    ssn: '',
    // recaptchaToken: '', // Commented out for now
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  // Form submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Handle form field changes
  const handleChange = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    const { name } = e.currentTarget;
    setFormState(prevState => ({ ...prevState, [name]: newValue || '' }));
  };

  // Handle date picker change
  const handleDateChange = (date: Date | null | undefined) => {
    setFormState(prevState => ({ ...prevState, dateOfBirth: date || null }));
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: IFormErrors = {};

    // Username validation
    if (!formState.username.trim()) {
      errors.username = 'Username is required';
    } else {
      const isLowerCase = /^[a-z]+$/.test(formState.username);
      if (!isLowerCase) {
        errors.username = 'Username must be all lowercase letters with no spaces';
      }
    }

    // Password validation
    if (!formState.password) {
      errors.password = 'Password is required';
    } else {
      const hasUpperCase = /[A-Z]/.test(formState.password);
      const hasLowerCase = /[a-z]/.test(formState.password);
      const hasNumber = /[0-9]/.test(formState.password);
      const isLongEnough = formState.password.length >= 12;

      if (!isLongEnough) {
        errors.password = 'Password must have at least 12 characters';
      } else if (!hasUpperCase) {
        errors.password = 'Password must contain at least 1 capital letter';
      } else if (!hasLowerCase) {
        errors.password = 'Password must contain at least 1 lowercase letter';
      } else if (!hasNumber) {
        errors.password = 'Password must contain at least 1 number';
      }
    }

    // Confirm password validation
    if (formState.password !== formState.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    // Date of birth validation
    if (!formState.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formState.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formState.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // SSN validation
    if (!formState.ssn.trim() || formState.ssn.includes('_')) {
      errors.ssn = 'Social Security Number is required';
    }

    // reCAPTCHA validation - Commented out for now
    /*
    if (!formState.recaptchaToken) {
      errors.recaptcha = 'Please complete the reCAPTCHA verification';
    }
    */

    // Update error state
    setFormErrors(errors);

    // Return true if no errors
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset submission states
    setSubmitSuccess(false);
    setSubmitError(null);

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Format SSN by removing hyphens
      const formattedSSN = formState.ssn.replace(/-/g, '');

      // Format date to ISO string
      const formattedDate = formState.dateOfBirth ? formState.dateOfBirth.toISOString() : '';

      // Call the live API endpoint
      const response = await fetch('https://prod-api.ubgretire.com/api/v1/Registration/Register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ssn: formattedSSN,
          dateOfBirth: formattedDate,
          username: formState.username,
          password: formState.password,
          emailAddress: formState.email,
        }),
      });

      if (!response.ok) {
        // Try to get detailed error message from response
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Registration failed');
        } catch (jsonError) {
          throw new Error(`Registration failed with status: ${response.status}`);
        }
      }

      // Show success message
      setSubmitSuccess(true);

      // Reset form state
      setFormState({
        username: '',
        password: '',
        confirmPassword: '',
        dateOfBirth: null,
        email: '',
        ssn: '',
        // recaptchaToken: '', // Commented out for now
      });

      // Reset reCAPTCHA - Commented out for now
      /*
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
      */

    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack tokens={stackTokens} className="ubg-registration-form">
      <Text variant="xLarge" className="form-title">Create Your Account</Text>

      {submitSuccess && (
        <MessageBar messageBarType={MessageBarType.success}>
          Your registration was successful! You may now log in.
        </MessageBar>
      )}

      {submitError && (
        <MessageBar messageBarType={MessageBarType.error}>
          {submitError}
        </MessageBar>
      )}

      <form onSubmit={handleSubmit}>
        <Stack tokens={stackTokens}>
          <TextField
            label="Username"
            name="username"
            value={formState.username}
            onChange={handleChange}
            required
            errorMessage={formErrors.username}
          />

          <TextField
            label="Email Address"
            name="email"
            type="email"
            value={formState.email}
            onChange={handleChange}
            required
            errorMessage={formErrors.email}
          />

          <TextField
            label="Password"
            name="password"
            type="password"
            value={formState.password}
            onChange={handleChange}
            required
            errorMessage={formErrors.password}
          />

          <TextField
            label="Re-Type Password"
            name="confirmPassword"
            type="password"
            value={formState.confirmPassword}
            onChange={handleChange}
            required
            errorMessage={formErrors.confirmPassword}
          />

          <DatePicker
            label="Date of Birth"
            isRequired
            strings={datePickerStrings}
            placeholder="Select a date..."
            ariaLabel="Select a date"
            value={formState.dateOfBirth || undefined}
            onSelectDate={handleDateChange}
            maxDate={new Date()}
            allowTextInput
            formatDate={date => date ? date.toLocaleDateString() : ''}
            showGoToToday={false}
            showMonthPickerAsOverlay={true}
            borderless={false}
          />
          {formErrors.dateOfBirth && (
            <Text className="error-message" style={{ color: '#a4262c' }}>
              {formErrors.dateOfBirth}
            </Text>
          )}

          <MaskedTextField
            label="Social Security Number"
            name="ssn"
            mask="999-99-9999"
            value={formState.ssn}
            onChange={handleChange}
            required
            errorMessage={formErrors.ssn}
          />

          {/* reCAPTCHA container - Commented out for now */}
          {/*
          <div className="recaptcha-container">
            <div id="recaptcha-container"></div>
            {formErrors.recaptcha && (
              <Text className="error-message" style={{ color: '#a4262c' }}>
                {formErrors.recaptcha}
              </Text>
            )}
          </div>
          */}

          <PrimaryButton
            type="submit"
            text="Register"
            disabled={isSubmitting}
            iconProps={{ iconName: isSubmitting ? undefined : 'UserFollowed' }}
          />

          {isSubmitting && (
            <Stack horizontalAlign="center">
              <Spinner size={SpinnerSize.medium} label="Registering..." />
            </Stack>
          )}
        </Stack>
      </form>
    </Stack>
  );
};

// Declare grecaptcha in global namespace - Keep this for future use
declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default RegistrationForm;